import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AnalyticsAudience,
  AnalyticsPlatform,
  SearchPreset,
} from "../api/analyticsAdmin";
import { SearchSuggestionsAdminService } from "../api/generated/services/SearchSuggestionsAdminService";
import { SearchSuggestionViewModel } from "../api/generated/models/SearchSuggestionViewModel";
import { BusinessCategory, listBusinessCategories } from "../api/businessCategories";
import { computeDateRange } from "../helpers/analyticsPlus";
import { indexSuggestionsByKeyword } from "../helpers/searchSuggestions";
import { WidgetBoundary } from "../components/AdminDashboard/WidgetBoundary";
import { ConfirmationModal } from "../components/ConfirmationModal";
import { Toast } from "../components/Toast";
import {
  SearchFilterBar,
  SearchFilters,
} from "../components/AnalyticsPlus/SearchFilterBar";
import SearchSuggestionEditor, {
  SearchSuggestionDraft,
} from "../components/AnalyticsPlus/SearchSuggestionEditor";
import SearchSuggestionsList from "../components/AnalyticsPlus/SearchSuggestionsList";
import StreamProgress from "../components/AnalyticsPlus/StreamProgress";
import ZeroResultQueries from "../components/AnalyticsPlus/ZeroResultQueries";
import { zeroResultQueriesSection } from "../components/AnalyticsPlus/searchSections";
import { useAnalyticsStream } from "../components/AnalyticsPlus/useAnalyticsStream";
import { useDebouncedValue } from "../components/AnalyticsPlus/useDebouncedValue";
import {
  fetchBusinessesByIds,
  TypesenseBusinessDocument,
} from "../utils/typesenseBusinessSearch";
import "../api/openApiConfig";

const FILTER_SETTLE_MS = 400;

type EditorTarget =
  | { suggestion: SearchSuggestionViewModel; presetKeyword?: undefined }
  | { suggestion: null; presetKeyword?: string };

const AdminZeroResultSuggestions: React.FC = () => {
  const initialRange = computeDateRange("last30", new Date());
  const [filters, setFilters] = useState<SearchFilters>({
    preset: "Last30Days",
    from: initialRange.from,
    to: initialRange.to,
    platform: "All",
    audience: "All",
  });
  const [minSearches, setMinSearches] = useState(3);

  const settled = useDebouncedValue(filters, FILTER_SETTLE_MS);
  const settledMinSearches = useDebouncedValue(minSearches, FILTER_SETTLE_MS);

  const sections = useMemo(
    () => ({ queryFunnel: zeroResultQueriesSection(settled, settledMinSearches) }),
    [settled, settledMinSearches]
  );
  const { state, progress } = useAnalyticsStream(sections);

  const [suggestions, setSuggestions] = useState<SearchSuggestionViewModel[] | null>(null);
  const [businesses, setBusinesses] = useState<Map<string, TypesenseBusinessDocument>>(
    new Map()
  );
  const [categories, setCategories] = useState<BusinessCategory[]>([]);
  const [editor, setEditor] = useState<EditorTarget | null>(null);
  const [deleting, setDeleting] = useState<SearchSuggestionViewModel | null>(null);
  const [deleteInFlight, setDeleteInFlight] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(
    null
  );

  const loadSuggestions = useCallback(async () => {
    try {
      const list = await SearchSuggestionsAdminService.getApiSearchSuggestionsAdmin();
      setSuggestions(list);
    } catch (error) {
      console.error("Error loading search suggestions:", error);
      setSuggestions([]);
      setToast({ message: "Could not load suggestions.", type: "error" });
    }
  }, []);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  useEffect(() => {
    listBusinessCategories()
      .then(setCategories)
      .catch((error) => console.error("Error loading business categories:", error));
  }, []);

  const categoryNames = useMemo(
    () => new Map(categories.map((category) => [category.id, category.name])),
    [categories]
  );

  // Merchant names and logos live in Typesense, not in the suggestion record.
  // Ids are asked for once: one that is missing from the index would otherwise
  // be re-requested on every render.
  const requestedIds = useRef(new Set<string>());
  useEffect(() => {
    if (!suggestions) return;
    const missing = Array.from(
      new Set(suggestions.flatMap((s) => s.businessIds ?? []))
    ).filter((id) => !requestedIds.current.has(id));
    if (missing.length === 0) return;
    missing.forEach((id) => requestedIds.current.add(id));
    fetchBusinessesByIds(missing)
      .then((docs) =>
        setBusinesses((prev) => {
          const next = new Map(prev);
          docs.forEach((doc) => next.set(doc.id, doc));
          return next;
        })
      )
      .catch((error) => console.error("Error loading merchants:", error));
  }, [suggestions]);

  const suggestionsByKeyword = useMemo(
    () => indexSuggestionsByKeyword(suggestions ?? []),
    [suggestions]
  );

  const saveSuggestion = async (draft: SearchSuggestionDraft) => {
    const body = { ...draft, note: draft.note.trim() || null };
    if (editor?.suggestion?.id) {
      await SearchSuggestionsAdminService.putApiSearchSuggestionsAdmin(
        editor.suggestion.id,
        body
      );
    } else {
      await SearchSuggestionsAdminService.postApiSearchSuggestionsAdmin(body);
    }
    setEditor(null);
    setToast({ message: "Suggestion saved.", type: "success" });
    await loadSuggestions();
  };

  const togglePublished = async (suggestion: SearchSuggestionViewModel) => {
    if (!suggestion.id) return;
    try {
      await SearchSuggestionsAdminService.putApiSearchSuggestionsAdmin(suggestion.id, {
        keywords: suggestion.keywords,
        businessIds: suggestion.businessIds,
        categoryIds: suggestion.categoryIds,
        isPublished: !suggestion.isPublished,
        note: suggestion.note,
      });
      await loadSuggestions();
    } catch (error: any) {
      setToast({
        message: error?.body?.message || "Could not update the suggestion.",
        type: "error",
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleting?.id) return;
    setDeleteInFlight(true);
    try {
      await SearchSuggestionsAdminService.deleteApiSearchSuggestionsAdmin(deleting.id);
      setDeleting(null);
      setToast({ message: "Suggestion deleted.", type: "success" });
      await loadSuggestions();
    } catch (error: any) {
      setToast({
        message: error?.body?.message || "Could not delete the suggestion.",
        type: "error",
      });
    } finally {
      setDeleteInFlight(false);
    }
  };

  const handlePresetChange = (preset: SearchPreset) =>
    setFilters((prev) => ({ ...prev, preset }));
  const handleCustomChange = (patch: Partial<Pick<SearchFilters, "from" | "to">>) =>
    setFilters((prev) => ({ ...prev, ...patch }));
  const handlePlatformChange = (platform: AnalyticsPlatform) =>
    setFilters((prev) => ({ ...prev, platform }));
  const handleAudienceChange = (audience: AnalyticsAudience) =>
    setFilters((prev) => ({ ...prev, audience }));

  return (
    <div>
      <h2 className="font-medium mb-1">Zero result suggestion</h2>
      <p className="text-sm text-gray-500 mb-6">
        Pair merchants or categories with queries that find nothing, so a
        searcher sees a suggestion instead of an empty page.
      </p>

      <SearchFilterBar
        filters={filters}
        onPresetChange={handlePresetChange}
        onCustomChange={handleCustomChange}
        onPlatformChange={handlePlatformChange}
        onAudienceChange={handleAudienceChange}
      />

      <StreamProgress progress={progress} />

      <WidgetBoundary name="Zero-result queries">
        <ZeroResultQueries
          section={state.queryFunnel}
          minSearches={minSearches}
          onMinSearchesChange={setMinSearches}
          suggestionsByKeyword={suggestionsByKeyword}
          onMap={(query) => setEditor({ suggestion: null, presetKeyword: query })}
          onEdit={(suggestion) => setEditor({ suggestion })}
        />
      </WidgetBoundary>

      <WidgetBoundary name="Suggestions">
        <SearchSuggestionsList
          suggestions={suggestions}
          businesses={businesses}
          categoryNames={categoryNames}
          onEdit={(suggestion) => setEditor({ suggestion })}
          onDelete={setDeleting}
          onTogglePublished={togglePublished}
        />
      </WidgetBoundary>

      <SearchSuggestionEditor
        isOpen={editor !== null}
        suggestion={editor?.suggestion ?? null}
        presetKeyword={editor?.presetKeyword}
        knownBusinesses={businesses}
        categories={categories}
        onClose={() => setEditor(null)}
        onSave={saveSuggestion}
      />

      <ConfirmationModal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title="Delete suggestion"
        message={`Delete the suggestion for "${(deleting?.keywords ?? []).join(", ")}"? Searchers will see an empty page for these queries again.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteInFlight}
      />

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
};

export default AdminZeroResultSuggestions;
